import { useMemo, useState, useEffect } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import styled from '@emotion/styled';
import {
    BodyTextSmall,
    HeadingText60,
    Icon,
    SkeletonPlaceholder,
    pxToRem,
    HeadingText40,
    Badge,
    ContentBanner,
} from '@modernhealth/vital-components';
import { TrackClick, useAnalytics } from '@modernhealth/web-analytics';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useHistory, Redirect, useLocation, useRouteMatch } from 'react-router-dom';
import { colors, layout } from '@modernhealth/vital-foundation';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import * as Sentry from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import queryString from 'query-string';
import uniqBy from 'lodash/uniqBy';

import { Dispatch } from 'src/config/rootStore';
import { Button } from 'src/shared/analytics/components';
import { ContentCollectionPlatform, InternalLinkNode } from 'src/generated/webapp_gql';
import { navigationService } from 'src/config/rootService';
import { useManageCareTeamScreenQuery } from 'src/modules/care/documents/manageCareTeam.generated';
import { ProviderDetailsModal } from 'src/modules/providers/components/ProviderDetailsModal';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { ChangeProviderModal } from 'src/modules/care/components/ChangeProviderModal';
import { RemoveProviderModal } from 'src/modules/care/components/RemoveProviderModal';
import { SOURCE_TYPES } from 'src/modules/user/models/user';
import { CrisisModal } from 'src/shared/components/CrisisModal';
import { ManageCareTeamTestID } from 'src/modules/care/pages/testIds';
import { ProviderAction } from '../components/ProviderAction';
import { PathwayCancelModalPage } from 'src/modules/pathways/pages/PathwayCancelModalPage';
import { captureException } from 'src/shared/helpers/captureException';
import { getConfigForKey } from 'src/shared/helpers/getConfigForKey';

interface ActionsGroupingInterface {
    ungroupedActions: InternalLinkNode[];
    groupedActions: InternalLinkNode[];
}

export const ManageCareTeam = () => {
    const { t } = useTranslation();
    const { path, isExact } = useRouteMatch();
    const analytics = useAnalytics();
    const history = useHistory();
    const { search } = useLocation();
    const dispatch = useDispatch<Dispatch>();
    const providers = useSelector(getMyProviders);
    const { removeUuid, changeUuid, profileUuid } = queryString.parse(search);
    const [isCrisisModalOpen, setIsCrisisModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const { data, loading, refetch } = useManageCareTeamScreenQuery({
        variables: {
            platform: ContentCollectionPlatform.Web,
        },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (isExact) {
            refetch();
        }
    }, [isExact, refetch]);

    useEffect(() => {
        dispatch.providers.fetchMyProviders();
    }, [dispatch]);

    const providerUuid = removeUuid || changeUuid || profileUuid;
    const selectedProvider = useMemo(
        () => providers.find(p => p.uuid === providerUuid),
        [providerUuid, providers],
    );
    const screenSection = data?.manageCareTeamScreen?.screenSections ?? [];

    const actions = filterNullish(
        (screenSection[0]?.contentCollectionLinks as InternalLinkNode[]) ?? [],
    );

    //separate actions into ungroupedActions and groupedActions
    const { ungroupedActions, groupedActions } = useMemo(() => {
        return actions.reduce<ActionsGroupingInterface>(
            (acc, action) => {
                acc[action.groupKey === null ? 'ungroupedActions' : 'groupedActions'].push(action);
                return acc;
            },
            { ungroupedActions: [], groupedActions: [] },
        );
    }, [actions]);

    //create tabs list
    const tabs = useMemo(
        () => uniqBy(groupedActions, action => action.groupKey).map(action => action.groupKey),
        [groupedActions],
    );
    //filter groupedActions by groupKey
    const filteredGroupedActions = useMemo(
        () => groupedActions.filter(action => action.groupKey === tabs[selectedTab]),
        [groupedActions, tabs, selectedTab],
    );

    if (loading) {
        return (
            <Container data-testid={ManageCareTeamTestID.Loader}>
                <SkeletonPlaceholder width={300} height={40} marginTop={40} />
                <SkeletonPlaceholder width={500} height={20} marginTop={14} marginBottom={84} />
                <ActionsContainer>
                    {[{}, {}, {}].map((_, index) => (
                        <ProviderActionItem key={`placeholder_${index}`} isFirst={index === 0}>
                            <SkeletonPlaceholder width={60} height={60} borderRadius={30} />
                            <ItemContent>
                                <SkeletonPlaceholder width={100} height={20} />
                                <SkeletonPlaceholder width={300} height={18} marginTop={4} />
                            </ItemContent>
                        </ProviderActionItem>
                    ))}
                </ActionsContainer>
            </Container>
        );
    }

    //right now we dont have to handle more than one section
    if (screenSection.length > 1) {
        Sentry.captureMessage('ManageCareTeam Screen received more than 1 screen section');
        datadogLogs.logger.info('ManageCareTeam Screen received more than 1 screen section');
    }

    if (screenSection.length === 0) {
        Sentry.captureMessage(
            'ManageCareTeam Screen received 0 screen sections',
            Sentry.Severity.Error,
        );
        captureException(new Error('ManageCareTeam Screen received 0 screen sections'));
        return <Redirect to="/" />;
    }

    const renderCollectionLinks = (collection: InternalLinkNode[]) => {
        return collection.map((action, index) => {
            return (
                <ProviderActionWrapper key={action.uuid}>
                    <ProviderAction action={action} elementIndex={index} />
                </ProviderActionWrapper>
            );
        });
    };

    const renderContentBanners = (collection: InternalLinkNode[]) => {
        return collection.map(action => {
            return !!action.buttonText ? (
                <ContentBanner
                    title={action.title}
                    description={action.subtitle || ''}
                    backgroundColor={getConfigForKey(
                        action.colorScheme,
                        {
                            BLUE: colors.blue65,
                        },
                        colors.blue65,
                    )}
                    onClick={() => navigationService.navigate(action.path)}
                    buttonTitle={action.buttonText}
                    image={action.image ?? undefined}
                    buttonProps={{
                        variant: 'secondary',
                    }}
                />
            ) : null;
        });
    };

    return (
        <Container>
            <TrackClick eventName="back button">
                <IconContainer onClick={() => history.goBack()}>
                    <Icon name="arrow-left" />
                </IconContainer>
            </TrackClick>
            <HeadingText60 centered css={{ marginBottom: pxToRem(13) }}>
                {t('care:manage_care_team.title')}
            </HeadingText60>
            <BodyTextSmall centered marginBottom={44}>
                {t('care:manage_care_team.description')}
            </BodyTextSmall>
            <ActionsContainer>
                {ungroupedActions.length > 0 && renderContentBanners(ungroupedActions)}

                {groupedActions.length > 0 && (
                    <>
                        <TabsWrapper>
                            {tabs.map((tab, index) => (
                                <TabElement
                                    key={index}
                                    isSelected={selectedTab === index}
                                    onClick={() => {
                                        analytics.trackEvent(
                                            EventLexicon.clickManageCareTeamNavigation({
                                                translationKey: tab as string,
                                            }),
                                        );

                                        setSelectedTab(index);
                                    }}
                                >
                                    <HeadingText40
                                        fontColor={selectedTab === index ? 'gray85' : 'gray40'}
                                    >
                                        <HeadingContent>
                                            {tab}
                                            {tab === 'Pathways' && (
                                                <BadgeContainer>
                                                    <Badge
                                                        variant="success"
                                                        title={t(
                                                            'pathways:manage_care_team.new_tab.label',
                                                        )}
                                                    />
                                                </BadgeContainer>
                                            )}
                                        </HeadingContent>
                                    </HeadingText40>
                                </TabElement>
                            ))}
                        </TabsWrapper>
                        {renderCollectionLinks(filteredGroupedActions)}
                    </>
                )}
            </ActionsContainer>
            <CrisisElementsContainer>
                <BodyTextSmall>{t('shared:crisis_support_banner.message')}</BodyTextSmall>
                <CrisisLinkButton
                    eventName="careNavigation:care_navigation_topic_step.view_crisis_resources"
                    onClick={() => {
                        setIsCrisisModalOpen(true);
                    }}
                    variant="flat"
                >
                    <BodyTextSmall underline fontColor="blue55" marginLeft={3}>
                        {t('careNavigation:care_navigation_topic_step.view_crisis_resources')}
                    </BodyTextSmall>
                </CrisisLinkButton>
            </CrisisElementsContainer>
            <CrisisModal
                visible={isCrisisModalOpen}
                onClose={() => {
                    setIsCrisisModalOpen(false);
                }}
                source={SOURCE_TYPES.CALLBACK_SCREEN}
            />
            {!!selectedProvider && (
                <>
                    <ProviderDetailsModal
                        isVisible={!!profileUuid}
                        providerUuid={selectedProvider.uuid}
                        onClose={() => navigationService.replace('/care-team')}
                    />
                    <RemoveProviderModal
                        isVisible={!!removeUuid}
                        providerUuid={selectedProvider.uuid}
                        onConfirm={async () => {
                            await refetch();
                        }}
                        onCancel={() => navigationService.replace('/care-team')}
                    />
                    <ChangeProviderModal
                        isVisible={!!changeUuid}
                        providerUuid={selectedProvider.uuid}
                        onCancel={() => {
                            navigationService.replace('/care-team');
                        }}
                    />
                </>
            )}
            <PathwayCancelModalPage.ModalRoute
                basePath={path}
                analytics={{ pageName: 'pathway-cancel-modal' }}
            />
        </Container>
    );
};

const Container = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
    marginTop: pxToRem(32),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const ActionsContainer = styled.div({
    width: '100%',
    maxWidth: pxToRem(650),
});

const ProviderActionItem = styled.div<{ isFirst: boolean }>(({ isFirst }) => ({
    margin: 0,
    paddingTop: pxToRem(39),
    paddingBottom: pxToRem(39),
    color: colors.gray65,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderTop: isFirst ? undefined : `${pxToRem(2)} solid ${colors.gray05}`,
}));

const ProviderActionWrapper = styled.div({
    paddingTop: pxToRem(28),
    paddingBottom: pxToRem(28),
    ':not(:first-of-type)': {
        borderTop: `${pxToRem(2)} solid ${colors.gray05}`,
    },
});

const IconContainer = styled.div({
    height: pxToRem(40),
    alignSelf: 'flex-start',
    cursor: 'pointer',
});

const ItemContent = styled.div({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: pxToRem(16),
});

const CrisisElementsContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: pxToRem(40),
});

const CrisisLinkButton = styled(Button)({
    padding: 0,
});

const TabsWrapper = styled.div({
    width: '100%',
    display: 'flex',
    marginTop: pxToRem(40),
});

interface TabElementProps {
    isSelected?: boolean;
}

const TabElement = styled.button<TabElementProps>(
    () => ({
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'baseline',
        paddingBottom: pxToRem(8),
        paddingLeft: 0,
        paddingRight: 0,
        position: 'relative',
        border: 'none',
        background: 'none',
        marginRight: pxToRem(28),
        '&:focus-visible': {
            outline: `1px solid ${colors.blue55}`,
        },
    }),
    props =>
        props.isSelected
            ? {
                  '&:after': {
                      position: 'absolute',
                      content: '""',
                      bottom: -2,
                      left: 0,
                      height: 3,
                      width: '105%',
                      backgroundColor: colors.blue55,
                  },
              }
            : {},
);

const HeadingContent = styled.div({
    display: 'flex',
    alignItems: 'center',
});

const BadgeContainer = styled.div({
    display: 'flex',
    marginLeft: pxToRem(8),
});
